import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted } from 'vue';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import HorizontalTitleList from '@/components/timeline/HorizontalTitleList.vue';

import { ImpressionTrackingEvents } from '@/enums/events';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { HorizontalTitleListItem } from '@/components/timeline/types';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type { ListTitleSR } from '@/features/sponsored-recommendations/types';

import { SraEventAction } from '@/@types/graphql-types';
import { ClickoutPlacement } from '@/interfaces/snowplow/clickout-context';


export default /*#__PURE__*/_defineComponent({
  __name: 'SRTitleListGraphql',
  props: {
    fullWidth: { type: Boolean },
    titles: null,
    sponsoredAd: null,
    additionalContexts: null,
    impressionTrackingLabel: null,
    impressionTrackingProperty: null,
    impressionTrackingCategory: null,
    showSponsoredAd: { type: Boolean },
    placement: null
  },
  emits: ["onPosterClick", "onPosterClickOut", "imageError"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'onPosterClick', data: { index: number; title: any; titleList?: string }): void;
	(e: 'onPosterClickOut', data: { index: number; title: any; titleList?: string; event: SraEventAction }): void;
	(e: 'imageError', src: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	fullWidth: boolean;
	titles: ListTitleSR[];
	sponsoredAd: SponsoredAdFragment | undefined;
	additionalContexts?: SnowplowContext[];
	impressionTrackingLabel?: string;
	impressionTrackingProperty?: string;
	impressionTrackingCategory?: any;
	showSponsoredAd: boolean;
	placement?: ClickoutPlacement;
};





onMounted(() => {
	if (props.titles?.length) trackSimilarTitlesLoaded();
});

const posterOverrides = computed(() => {
	if (!props.sponsoredAd?.campaign) return null;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];
	return Object.fromEntries(overrides.map(({ nodeId: id, promotionalImageUrl: url }) => [id, url]));
});

function transformPosterProps(posterProps: PosterGraphqlProps, title: HorizontalTitleListItem): PosterGraphqlProps {
	const posterUrl = posterOverrides.value?.[title.id];
	if (posterUrl == null) return posterProps;

	return {
		...posterProps,
		sponsoredRecImages: {
			imageUrl: posterUrl,
			sourceSets: [
				{
					srcSet: posterUrl?.replace(/\.\w+$/, '.avif'),
					imageProp: {
						extension: '.avif',
						type: 'image/avif',
					},
					sourceProp: {
						viewport: 'desktop',
					},
					media: null,
				},
			],
		},
		title: {
			...title,
			content: {
				...title.content,
				posterUrl,
			},
		},
	};
}

const transformedTitles = computed(() => {
	if (!props.sponsoredAd?.campaign) return props.titles;

	const overrides = props.sponsoredAd?.campaign?.nodeOverrides ?? [];

	const mapTitleIdWithUrl = Object.fromEntries(
		overrides.map(({ nodeId: id, watchNowOffer }) => [id, watchNowOffer?.standardWebURL])
	);

	// If we have a overwriten watchNowOffer unique to the title, we want to
	// inject that into the title object, replacing content.watchNowOffer
	return props.titles.map(title => {
		if (mapTitleIdWithUrl[title.id]) {
			title.content.fullPath = mapTitleIdWithUrl[title.id] || title.content.fullPath;
		}

		return title;
	});
});

function trackSimilarTitlesLoaded() {
	TrackingHelper.trackEvent(
		ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS,
		{
			action: 'loaded',
			nonInteraction: true,
			label: props.impressionTrackingLabel ?? undefined,
		},
		props.additionalContexts
	);
}

function trackScroll(label: string) {
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'horizontal_scroller_clicked',
			label,

			property: props.impressionTrackingLabel ?? undefined,
		},
		props.additionalContexts
	);
}

return { __sfc: true,props, emit, posterOverrides, transformPosterProps, transformedTitles, trackSimilarTitlesLoaded, trackScroll, PosterGraphql, HorizontalTitleList }
}

})